/**
 Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, type EntityState } from "@reduxjs/toolkit";
import type { RootState } from "..";
import { authenticationService, operationService, supportChannelServices, systemService } from "../../services";
import { SupportChannel } from "../../types/SupportChannel";
import { DEFAULT_LANDING_PAGE_ENUM, System } from "../../types/systemSetting";
import { Service } from "~/types/Service";
import { OperationDetails } from "~/types/operations";
import { createSocketPatchReducer } from "../events/socketPatch";

type SettingsState = {
  system: EntityState<System>;
  services: EntityState<Service>;
  support: EntityState<SupportChannel>;
  operations: EntityState<OperationDetails>;
  loading: boolean;
  systemSettingsLoading: boolean;
  error: string | null;
};

export const systemSettingsAdapter = createEntityAdapter<System>({
  selectId: (system) => system.name,
});
export const settingsServicesAdapter = createEntityAdapter<Service>();
export const settingsOperationsAdapter = createEntityAdapter<OperationDetails>();
export const supportChannelAdapter = createEntityAdapter<SupportChannel>();

const initialState: SettingsState = {
  system: systemSettingsAdapter.getInitialState(),
  services: settingsServicesAdapter.getInitialState(),
  operations: settingsOperationsAdapter.getInitialState(),
  support: supportChannelAdapter.getInitialState(),
  loading: true,
  systemSettingsLoading: true,
  error: null,
};

export const fetchSystemSettings = createAsyncThunk("settings/fetchSystemSettings", async () => {
  const response = await systemService.getSystemSettingsData();
  return response;
});

export const fetchSupportChannel = createAsyncThunk("settings/fetchSupportChannel", async () => {
  const result = await supportChannelServices.getSupportChannel();
  return result;
});

export const fetchSystemServices = createAsyncThunk("settings/fetchServices", async () => {
  const response = await systemService.getServices();
  return response;
});

export const fetchOperations = createAsyncThunk("settings/fetchOperations", async (status?: string) => {
  const response = await operationService.getAllOperations({
    status: status ?? undefined
  });
  return response;
});



const SettingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    applySocketPatchesSystem: createSocketPatchReducer(systemSettingsAdapter, (s) => s.system),
    applySocketPatchesSupport: createSocketPatchReducer(supportChannelAdapter, (s) => s.support),
    applySocketPatchesOperations: createSocketPatchReducer(settingsOperationsAdapter, (s) => s.operations, { upsertOnMissing: true }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemSettings.fulfilled, (state, action) => {
        systemSettingsAdapter.setAll(state.system, action.payload);
        state.systemSettingsLoading = false;
      })
      .addCase(fetchSystemSettings.pending, (state) => {
        state.systemSettingsLoading = true;
      })
      .addCase(fetchSystemSettings.rejected, (state, action) => {
        state.systemSettingsLoading = false;
        state.error = action.error.message ?? "Something went wrong";
      })

      // Support Channel Fetch
      .addCase(fetchSupportChannel.fulfilled, (state, action) => {
        supportChannelAdapter.setAll(state.support, action.payload);
        state.loading = false;
      })
      .addCase(fetchSupportChannel.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSupportChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Something went wrong";
      })

      // VMC service Fetch
      .addCase(fetchSystemServices.fulfilled, (state, action) => {
        settingsServicesAdapter.setAll(state.services, action.payload);
        state.loading = false;
      })
      .addCase(fetchSystemServices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSystemServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Something went wrong";
      })

      // Operations Fetch
      .addCase(fetchOperations.fulfilled, (state, action) => {
        settingsOperationsAdapter.setAll(state.operations, action.payload);
      });
  },
});

export const settingsActions = SettingsSlice.actions;
export default SettingsSlice.reducer;

const settingsSystemEntitySelectors = systemSettingsAdapter.getSelectors((state: RootState) => state.settings.system,);
const settingsServicesEntitySelectors = settingsServicesAdapter.getSelectors((state: RootState) => state.settings.services);
const settingsOperationsEntitySelectors = settingsOperationsAdapter.getSelectors((state: RootState) => state.settings.operations);
const settingsSupportEntitySelectors = supportChannelAdapter.getSelectors((state: RootState) => state.settings.support);

export const settingsSystem = settingsSystemEntitySelectors.selectAll;
export const settingsSystemEntities = settingsSystemEntitySelectors.selectEntities;

/** Memoized per status so useAppSelector can bail out when the filter result is unchanged. */
const settingsOperationsByStatus = new Map<string, (state: RootState) => OperationDetails[]>();

export const settingsOperations = (status?: string) => {
  if (!status) {
    return settingsOperationsEntitySelectors.selectAll;
  }
  let selector = settingsOperationsByStatus.get(status);
  if (!selector) {
    selector = createSelector(
      [settingsOperationsEntitySelectors.selectAll],
      (operations) => operations.filter((operation) => operation.status === status),
    );
    settingsOperationsByStatus.set(status, selector);
  }
  return selector;
};

const settingsRadioMonitoringAll = createSelector(
  [settingsOperationsEntitySelectors.selectAll],
  (operations) =>
    operations.filter(
      (operation) => operation.objectType === "radio" && operation.operationType === "START_RADIO_MONITORING",
    ),
);

/** Memoized per radioId — avoids a new filtered array identity on every store tick. */
const settingsRadioMonitoringByRadioId = new Map<string, (state: RootState) => OperationDetails[]>();

export const settingsRadioMonitoringOperations = (radioId?: string) => {
  if (!radioId) {
    return settingsRadioMonitoringAll;
  }
  let selector = settingsRadioMonitoringByRadioId.get(radioId);
  if (!selector) {
    selector = createSelector(
      [settingsRadioMonitoringAll],
      (operations) => operations.filter((operation) => operation.objectId === radioId),
    );
    settingsRadioMonitoringByRadioId.set(radioId, selector);
  }
  return selector;
};

export const settingsSupport = settingsSupportEntitySelectors.selectAll;
export const settingsOperationsEntities = settingsOperationsEntitySelectors.selectEntities;
export const settingsSupportEntities = settingsSupportEntitySelectors.selectEntities;

export const settingsServices = settingsServicesEntitySelectors.selectAll;
export const settingsServicesEntities = settingsServicesEntitySelectors.selectEntities;

const getSystemSetting = (name: string) => (state: RootState): System | undefined => settingsSystemEntities(state)[name];

const getSystemFlag = (name: string) => (state: RootState): boolean => Boolean(getSystemSetting(name)(state)?.value);

export const settingsSystemDashboardConsoleEnabled = (state: RootState) => getSystemFlag("dashboard.console.enabled")(state);
export const settingsSystemOnboardingEnabled = (state: RootState) => getSystemFlag("dashboard.getting.started.enabled")(state);
export const settingsSystemNewWizardUiEnabled = (state: RootState) => getSystemFlag("getting.started.new.wizard.ui.enabled")(state);
export const settingsAssistantEnabled = (state: RootState) => authenticationService.isOperator() || getSystemFlag("system.chatbot.enabled")(state);
export const settingsAirgapEnabled = (state: RootState) => getSystemFlag("airgap.enabled")(state);
export const settingsDashboardInsightsEnabled = (state: RootState) => authenticationService.isOperator() || getSystemFlag("dashboard.insights.enabled")(state);
export const settingsChannelPlanInsightsEnabled = (state: RootState) => getSystemFlag("channelPlan.insights.enabled")(state);
export const settingsLoading = (state: RootState) => state.settings.systemSettingsLoading;
export const settingsDefaultLandingPage = (state: RootState): DEFAULT_LANDING_PAGE_ENUM => getSystemSetting("default.landing.page")(state)?.value;

export const settingsDeviceInsightsEnabled = (state: RootState) => getSystemFlag("deviceInfo.insights.enabled")(state);
export const settingsDeviceTimelineCoreEventsEnabled = (state: RootState) => getSystemFlag("device.timeline.coreevents.enabled")(state);
export const settingsDeviceConnectionQualityEnabled = (state: RootState) => getSystemFlag("device.metric.connectionQuality.enabled")(state);
export const settingsDeviceUsageReportEnabled = (state: RootState) =>
  authenticationService.isOperator() || getSystemFlag("device.usage.report.enabled")(state);

export const settingsRadioMapGroupingEnabled = (state: RootState) => getSystemFlag("radio.indoor.map.grouping.enabled")(state);
export const settingsOutdoorGroupFloorPlanEnabled = (state: RootState) => getSystemFlag("radio.outdoor.group.floorplan.enabled")(state);
export const settingsRadioWalkTestEnabled = (state: RootState) => getSystemFlag("radio.walktest.enabled")(state);

// Operators always have access; tenants need the vendor-specific flag.
export const settingsRadioMonitoringAirspanEnabled = (state: RootState) =>
  authenticationService.isOperator() || getSystemFlag("radio.monitoring.enabled")(state);
export const settingsRadioMonitoringClassMEnabled = (state: RootState) =>
  authenticationService.isOperator() || getSystemFlag("radio.monitoring.classm.enabled")(state);

export const settingsRadioNeighborAutoAssignDefault = (state: RootState) => getSystemFlag("radio.neighbor.autoassign.default")(state);
export const settingsAnalysedMetricsEnabled = (state: RootState) => getSystemFlag("kpi.analysed.metrics.enabled")(state);
export const settingsEdgeNodeInfoReportingEnabled = (state: RootState) => getSystemFlag("edge.nodeInfo.reporting.enabled")(state);
export const settingsRadioMultiTacEnabled = (state: RootState) => getSystemFlag("radio.multi.tac.enabled")(state);
export const settingsRadioBandwidthBW5And15Supported = (state: RootState) => getSystemFlag("radio.bandwidth.BW5AND15.supported")(state);
export const settingsPtpClockLicenseWarningsEnabled = (state: RootState) => getSystemFlag("clock.ptp.license.warnings.enabled")(state);

export const settingsServiceClassMEnabled = (state: RootState) => settingsServices(state).some((service) => service.serviceType === "CLASS_M");
export const settingsServiceAPP_INSIGHTEnabled = (state: RootState) => settingsServices(state).some((service) => service.serviceType === "APP_INSIGHT");
